import React, { useState, useRef, useCallback } from 'react';
import { motion } from 'framer-motion';
import emailjs from '@emailjs/browser';
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import SectionReveal from '../components/SectionReveal';

// TODO: move back to env vars once confirmed working
const EMAILJS_SERVICE_ID = 'service_zo2u2nh';
const EMAILJS_TEMPLATE_ID = 'template_qx4cuaa';
const EMAILJS_PUBLIC_KEY = 'GGVRBC41-_nAYvKvN';
const RECAPTCHA_SITE_KEY = '6Le_EXosAAAAADHD7VNjmeVA9hSvXUhnbF41FGop';

const ContactForm = () => {
  const formRef = useRef();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [status, setStatus] = useState('idle'); // idle | sending | success | error
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    business: '',
    role: '',
    email: '',
    phone: '',
    message: '',
  });

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    if (!executeRecaptcha) return;

    setStatus('sending');

    try {
      const token = await executeRecaptcha('contact_form');

      await emailjs.sendForm(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        formRef.current,
        { publicKey: EMAILJS_PUBLIC_KEY }
      );

      setStatus('success');
      setFormData({ first_name: '', last_name: '', business: '', role: '', email: '', phone: '', message: '' });
    } catch (err) {
      console.error('EmailJS error:', err);
      setStatus('error');
    }
  }, [executeRecaptcha]);

  return (
    <form ref={formRef} className="contact-form" onSubmit={handleSubmit}>
      <div className="contact-form__row">
        <div className="contact-form__field">
          <label htmlFor="first_name">First Name *</label>
          <input id="first_name" name="first_name" required value={formData.first_name} onChange={handleChange} placeholder="Jane" />
        </div>
        <div className="contact-form__field">
          <label htmlFor="last_name">Last Name *</label>
          <input id="last_name" name="last_name" required value={formData.last_name} onChange={handleChange} placeholder="Doe" />
        </div>
      </div>

      <div className="contact-form__row">
        <div className="contact-form__field">
          <label htmlFor="business">Business Name *</label>
          <input id="business" name="business" required value={formData.business} onChange={handleChange} placeholder="Acme Inc." />
        </div>
        <div className="contact-form__field">
          <label htmlFor="role">Your Role</label>
          <input id="role" name="role" value={formData.role} onChange={handleChange} placeholder="CTO" />
        </div>
      </div>

      <div className="contact-form__row">
        <div className="contact-form__field">
          <label htmlFor="email">Email Address *</label>
          <input id="email" name="email" type="email" required value={formData.email} onChange={handleChange} placeholder="jane@acme.com" />
        </div>
        <div className="contact-form__field">
          <label htmlFor="phone">Phone *</label>
          <input id="phone" name="phone" type="tel" required value={formData.phone} onChange={handleChange} placeholder="(555) 123-4567" />
        </div>
      </div>

      <div className="contact-form__field contact-form__field--full">
        <label htmlFor="message">Any specific workflow pain points you'd like to share</label>
        <textarea id="message" name="message" rows="5" value={formData.message} onChange={handleChange} placeholder="Tell us about your current challenges..." />
      </div>

      <button
        type="submit"
        className="btn btn--primary btn--lg contact-form__submit"
        disabled={status === 'sending'}
      >
        {status === 'sending' ? 'Sending...' : 'Send Message'}
        {status !== 'sending' && (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" /></svg>
        )}
      </button>

      {status === 'success' && (
        <motion.p className="contact-form__status contact-form__status--success" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
          Message sent! We'll be in touch soon to schedule your AI Dream Call.
        </motion.p>
      )}
      {status === 'error' && (
        <motion.p className="contact-form__status contact-form__status--error" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
          Something went wrong. Please try again or email us directly.
        </motion.p>
      )}
    </form>
  );
};

const Contact = () => (
  <>
    <section className="page-hero">
      <div className="container">
        <motion.p
          className="section__eyebrow"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          Get In Touch
        </motion.p>
        <motion.h1
          className="page-hero__title"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.1 }}
        >
          Book Your Free <span className="gradient-text">AI Dream Call</span>
        </motion.h1>
        <motion.p
          className="page-hero__sub"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.25 }}
        >
          Submit your details and our team will reach out to schedule your call — your first step
          toward transforming the way your organization works.
        </motion.p>
      </div>
    </section>

    <section className="section contact-section">
      <div className="container">
        <SectionReveal>
          <div className="contact-wrapper">
            <div className="contact-info">
              <div className="contact-info__card">
                <div className="contact-info__icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
                </div>
                <h3>Email Us</h3>
                <a href="mailto:info@gofullspeed.ai">info@gofullspeed.ai</a>
              </div>
              <div className="contact-info__card">
                <div className="contact-info__icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
                </div>
                <h3>Based In</h3>
                <p>Los Angeles, CA</p>
              </div>
              <div className="contact-info__partners">
                <p>Proudly partnered with</p>
                <div className="contact-info__partner-logos">
                  <img src="/images/logos/dell-technologies.png" alt="Dell Technologies" className="contact-info__partner-img contact-info__partner-img--dell" />
                  <img src="/images/logos/nvidia.png" alt="NVIDIA" className="contact-info__partner-img contact-info__partner-img--nvidia" />
                </div>
              </div>
            </div>

            <GoogleReCaptchaProvider reCaptchaKey={RECAPTCHA_SITE_KEY}>
              <ContactForm />
            </GoogleReCaptchaProvider>
          </div>
        </SectionReveal>
      </div>
    </section>
  </>
);

export default Contact;
